import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import { type DocsListItem, docsListItemRemoteEntry } from "../document-list";
import { HttpRequestLoadError } from "./errors";
import { $loadHttpRequestPage, $setHttpRequestPageError } from "./request-page.atom";

const logger = new Logger("load-http-request-from-remote");

export interface LoadHttpRequestFromRemoteInput {
	key: string;
	document: DocsListItem;
	pageId: string;
	pagePath?: string;
	ref: string;
	ssr: boolean;
}

/**
 * Loads an HTTP request page from the remote DocsAPI (git.moment.dev).
 */
export const $loadHttpRequestFromRemote = atom(
	null,
	async (get, set, input: LoadHttpRequestFromRemoteInput) => {
		const { key, document, pageId, pagePath, ref, ssr } = input;
		const documentId = document.id;

		try {
			// Get the remote entry from the document.
			const workspaceName = docsListItemRemoteEntry(document)?.workspace.name;
			if (!workspaceName) {
				throw new Error("Document has no remote workspace");
			}

			// Get access token if available.
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const token = getAccessTokenSilently ? await getAccessTokenSilently() : undefined;

			// Fetch the page content from DocsAPI.
			const request = await get($docsApi).document.page.readHttpRequest(
				{ workspaceName, documentId, pageId, pagePath, ref },
				token
			);

			logger.info("Loaded HTTP request page from remote", { key, documentId, pageId, ref });

			// Load the HTTP request page (read-only, no persistence).
			set($loadHttpRequestPage, {
				key,
				record: {
					source: "remote",
					request,
					importEpoch: 0,
				},
				ssr,
			});
		} catch (err) {
			const error = new HttpRequestLoadError(err);
			logger.error("Failed to load HTTP request page from remote", {
				documentId,
				pageId,
				err,
			});
			set($setHttpRequestPageError, { key, error });
		}
	}
);
$loadHttpRequestFromRemote.debugLabel = "loadHttpRequestFromRemote";
